import type { Block } from "@blocknote/core";

/**
 * Parse a raw concept string into BlockNote blocks.
 *
 * Handles three cases:
 * - null/empty: returns undefined (BlockNote renders default empty paragraph)
 * - valid JSON array: parsed as BlockNote blocks
 * - plain string (legacy): wrapped in a single paragraph block
 */
export function parseConceptContent(
  raw: string | null | undefined
): Block[] | undefined {
  if (!raw || raw.trim() === "") return undefined;

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed as Block[];
    }
  } catch {
    // Not JSON, treat as plain text
  }

  return [
    {
      id: "legacy-text",
      type: "paragraph",
      props: {},
      content: [{ type: "text", text: raw, styles: {} }],
      children: [],
    } as unknown as Block,
  ];
}

/**
 * Remove empty paragraph blocks for read-only display.
 * Editors keep the author's blank lines; viewers collapse them so
 * stored double-newlines don't render as large gaps.
 */
export function stripEmptyBlocks(
  blocks: Block[] | undefined
): Block[] | undefined {
  if (!blocks) return undefined;

  const kept = blocks.filter((block) => {
    if (block.type !== "paragraph") return true;
    if (block.children && block.children.length > 0) return true;
    return extractTextFromBlock(block).length > 0;
  });

  return kept.length > 0 ? kept : undefined;
}

/**
 * Extract plain text from a raw concept field for list view previews.
 * Returns the first `maxLength` characters of text content.
 */
export function getPlainTextPreview(
  raw: string | null | undefined,
  maxLength = 120
): string {
  if (!raw || raw.trim() === "") return "";

  // Try to parse as BlockNote JSON
  let blocks: Block[];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return raw.slice(0, maxLength);
    blocks = parsed;
  } catch {
    // Plain text, return directly
    return raw.length > maxLength ? raw.slice(0, maxLength) + "..." : raw;
  }

  const text = extractTextFromBlocks(blocks);
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
}

export interface StructuredBlock {
  type: "paragraph" | "heading" | "bullet" | "numbered" | "check";
  text: string;
  level?: number;
}

/**
 * Extract structured preview blocks from a raw concept field.
 * Preserves block types (headings, bullets, etc.) for richer card previews.
 * Returns up to `maxBlocks` blocks.
 */
export function getStructuredPreview(
  raw: string | null | undefined,
  maxBlocks = 6
): StructuredBlock[] {
  if (!raw || raw.trim() === "") return [];

  let blocks: Block[];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [{ type: "paragraph", text: raw.slice(0, 200) }];
    }
    blocks = parsed;
  } catch {
    return [{ type: "paragraph", text: raw.slice(0, 200) }];
  }

  const result: StructuredBlock[] = [];
  let numberedIndex = 0;

  for (const block of blocks) {
    if (result.length >= maxBlocks) break;

    const text = extractTextFromBlock(block);
    if (!text) continue;

    const blockType = block.type as string;

    if (blockType === "heading") {
      const level = (block.props as Record<string, unknown>)?.level as number | undefined;
      result.push({ type: "heading", text, level: level ?? 1 });
      numberedIndex = 0;
    } else if (blockType === "bulletListItem") {
      result.push({ type: "bullet", text });
      numberedIndex = 0;
    } else if (blockType === "numberedListItem") {
      numberedIndex++;
      result.push({ type: "numbered", text, level: numberedIndex });
    } else if (blockType === "checkListItem") {
      result.push({ type: "check", text });
      numberedIndex = 0;
    } else {
      result.push({ type: "paragraph", text });
      numberedIndex = 0;
    }
  }

  return result;
}

function extractTextFromBlock(block: Block): string {
  const content = (block.content as Array<Record<string, unknown>>) || [];
  const text = content
    .map((inline) => {
      if (inline.type === "text") return inline.text as string;
      if (inline.type === "link") {
        const linkContent = (inline.content as Array<Record<string, unknown>>) || [];
        return linkContent.map((c) => c.text as string).join("");
      }
      return "";
    })
    .join("");

  const childText =
    block.children && block.children.length > 0
      ? " " + extractTextFromBlocks(block.children as Block[])
      : "";

  return (text + childText).trim();
}

function extractTextFromBlocks(blocks: Block[]): string {
  return blocks
    .map((block) => {
      const content = (block.content as Array<Record<string, unknown>>) || [];
      const text = content
        .map((inline) => {
          if (inline.type === "text") return inline.text as string;
          if (inline.type === "link") {
            const linkContent = (inline.content as Array<Record<string, unknown>>) || [];
            return linkContent.map((c) => c.text as string).join("");
          }
          return "";
        })
        .join("");

      const childText =
        block.children && block.children.length > 0
          ? " " + extractTextFromBlocks(block.children as Block[])
          : "";

      return text + childText;
    })
    .filter(Boolean)
    .join(" ");
}
